/**
 * PresenceForm — focused modal for planning an employee's presence (entrada
 * y salida). Two modes:
 *
 *  - Create: choose a journey pattern (full / morning / afternoon / double /
 *    custom) which produces one or more time slots (start–end), and a scope
 *    (just today / this week / this month) optionally restricted to selected
 *    weekdays. The form expands into N ScheduledItems and persists them with
 *    a single bulk call.
 *  - Edit: a single existing ScheduledItem; just date + start + end + notes,
 *    with a Delete option.
 *
 * Designed to be much simpler than the generic planning ScheduledForm — there
 * is no service/activity/client field here because presence planning is just
 * "this employee works between these hours that day".
 */
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { plannerApi, type PlannerResource, type PlannerScheduledItem, type PresenceRecord } from '@/services/api';
import { LoadingSpinner } from '@/components/common';

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

interface Slot { start: string; end: string } // 'HH:MM' (local)
type Pattern = 'full' | 'morning' | 'afternoon' | 'double' | 'custom';
type Scope = 'day' | 'week' | 'month';

const PRESETS: Record<Pattern, Slot[]> = {
  full: [{ start: '09:00', end: '18:00' }],
  morning: [{ start: '09:00', end: '14:00' }],
  afternoon: [{ start: '15:00', end: '20:00' }],
  double: [{ start: '09:00', end: '13:00' }, { start: '16:00', end: '20:00' }],
  custom: [{ start: '09:00', end: '17:00' }],
};

const PATTERN_LABEL: Record<Pattern, string> = {
  full: 'Jornada completa',
  morning: 'Media jornada (mañana)',
  afternoon: 'Media jornada (tarde)',
  double: 'Doble turno (mañana + tarde)',
  custom: 'Personalizado',
};

const WEEKDAY_LABEL = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function parseYmd(s: string): Date { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); }
function fmtTimeFromIso(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
function combine(date: Date, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  const x = new Date(date);
  x.setHours(h, m, 0, 0);
  return x;
}
function isoWeekStart(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const wd = x.getDay(); // 0=dom..6=sáb
  const diff = (wd + 6) % 7; // 0=lun..6=dom
  x.setDate(x.getDate() - diff);
  return x;
}
function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function jsDayToIso(jsDay: number): number { return (jsDay + 6) % 7; } // 0=dom→6, 1=lun→0,...

export default function PresenceForm({ resource, initialDate, initial, dayPresence, onClose, onSaved }: {
  resource: PlannerResource;
  initialDate: Date;             // the cell day the user clicked
  initial: PlannerScheduledItem | null;
  dayPresence?: PresenceRecord[]; // actual fichajes recorded that day (read-only view)
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!initial;

  // Create-mode state
  const [date, setDate] = useState<string>(ymdLocal(initialDate));
  const [pattern, setPattern] = useState<Pattern>('full');
  const [slots, setSlots] = useState<Slot[]>(PRESETS.full);
  const [scope, setScope] = useState<Scope>('day');
  const [weekdays, setWeekdays] = useState<boolean[]>([true, true, true, true, true, false, false]); // L-V default
  const [notes, setNotes] = useState('');

  // Edit-mode state (one item, just date + times)
  const [editDate, setEditDate] = useState<string>(initial ? ymdLocal(new Date(initial.start)) : ymdLocal(initialDate));
  const [editStart, setEditStart] = useState<string>(initial ? fmtTimeFromIso(initial.start) : '09:00');
  const [editEnd, setEditEnd] = useState<string>(initial ? fmtTimeFromIso(initial.end) : '18:00');
  const [editNotes, setEditNotes] = useState<string>(initial?.notes || '');

  const [saving, setSaving] = useState(false);
  const [closingOut, setClosingOut] = useState(false);

  // Show the "Cerrar salida ahora" button whenever the day is today AND the
  // employee currently has an OPEN entrada — i.e. the last chronological
  // fichaje of the day is an 'in'. This supports multiple tramos a day
  // (09:00 in → 14:00 out → 15:00 in → still open) instead of only the
  // single-shift case (hasIn && !hasOut).
  const isToday = ymdLocal(initialDate) === ymdLocal(new Date());
  const lastFichaje = (dayPresence || [])
    .slice()
    .sort((a, b) => +new Date(a.date) - +new Date(b.date))
    .at(-1);
  const hasOpenIn = lastFichaje?.type === 'in';
  const canCloseOutNow = isToday && hasOpenIn && !!resource.employeeNif;

  const closeOutNow = async () => {
    const nowTxt = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    if (!confirm(`Se registrará la salida del empleado ahora (${nowTxt}). ¿Continuar?`)) return;
    setClosingOut(true);
    try {
      await plannerApi.registerManualOut({ resourceId: resource._id });
      toast.success('Salida registrada');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al cerrar la salida');
    } finally {
      setClosingOut(false);
    }
  };

  const choosePattern = (p: Pattern) => {
    setPattern(p);
    setSlots(JSON.parse(JSON.stringify(PRESETS[p])));
  };
  const updateSlot = (i: number, field: 'start' | 'end', v: string) => {
    setSlots((arr) => arr.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)));
    setPattern('custom');
  };
  const addSlot = () => { setSlots((arr) => [...arr, { start: '15:00', end: '18:00' }]); setPattern('custom'); };
  const removeSlot = (i: number) => { setSlots((arr) => arr.filter((_, idx) => idx !== i)); setPattern('custom'); };

  // Compute target dates (create mode) from scope + weekdays.
  const targetDates = useMemo<Date[]>(() => {
    if (isEdit) return [];
    const base = parseYmd(date);
    if (scope === 'day') return [base];
    const start = scope === 'week' ? isoWeekStart(base) : startOfMonth(base);
    const end = scope === 'week' ? new Date(start.getTime() + 7 * 86400000) : new Date(endOfMonth(base).getTime() + 86400000);
    const out: Date[] = [];
    for (let d = new Date(start); d < end; d = new Date(d.getTime() + 86400000)) {
      const isoWd = jsDayToIso(d.getDay());
      if (weekdays[isoWd]) out.push(new Date(d));
    }
    return out;
  }, [isEdit, date, scope, weekdays]);

  const itemCount = targetDates.length * slots.length;

  const saveCreate = async () => {
    if (slots.length === 0) { toast.error('Añade al menos un tramo'); return; }
    if (targetDates.length === 0) { toast.error('Selecciona al menos un día'); return; }
    // Validate slots
    for (const s of slots) {
      if (!s.start || !s.end || s.start >= s.end) {
        toast.error('Cada tramo debe tener hora de inicio < hora de fin');
        return;
      }
    }
    setSaving(true);
    try {
      const items = targetDates.flatMap((d) => slots.map((s) => ({
        resourceId: resource._id,
        start: combine(d, s.start).toISOString(),
        end: combine(d, s.end).toISOString(),
        notes: notes.trim() || undefined,
        kind: 'presence' as const,
      })));
      if (items.length === 1) {
        await plannerApi.createScheduled(items[0]);
      } else {
        await plannerApi.createScheduledBulk(items);
      }
      toast.success(`${items.length} turno${items.length === 1 ? '' : 's'} creado${items.length === 1 ? '' : 's'}`);
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!initial) return;
    if (!editStart || !editEnd || editStart >= editEnd) {
      toast.error('La hora de fin debe ser posterior a la de inicio');
      return;
    }
    setSaving(true);
    try {
      const d = parseYmd(editDate);
      await plannerApi.updateScheduled(initial._id, {
        start: combine(d, editStart).toISOString(),
        end: combine(d, editEnd).toISOString(),
        notes: editNotes.trim() || undefined,
      });
      toast.success('Turno actualizado');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!initial) return;
    if (!confirm('¿Borrar este turno?')) return;
    setSaving(true);
    try {
      await plannerApi.deleteScheduled(initial._id);
      toast.success('Turno borrado');
      onSaved();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al borrar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Editar turno' : 'Nuevo turno de presencia'}</h2>
            <p className="text-sm text-gray-500">{resource.name}{resource.employeeNif ? ` · NIF ${resource.employeeNif}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Fichajes ya registrados ese día (read-only). Visible siempre que
              haya alguno, en cualquiera de los dos modos: ayuda a contrastar
              lo planificado con lo realmente fichado. */}
          {(dayPresence && dayPresence.length > 0) || canCloseOutNow ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
              {dayPresence && dayPresence.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    Fichajes registrados ese día ({dayPresence.length})
                  </p>
                  <div className="space-y-1">
                    {dayPresence
                      .slice()
                      .sort((a, b) => +new Date(a.date) - +new Date(b.date))
                      .map((p) => (
                        <p key={p._id} className="text-sm text-gray-800">
                          {p.type === 'in' ? '⏵ Entrada' : '⏸ Salida'}{' '}
                          <span className="font-mono">
                            {new Date(p.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {p.source === 'auto' && <span className="ml-1 text-[10px] text-gray-500">(auto)</span>}
                          {p.source === 'manual' && <span className="ml-1 text-[10px] text-gray-500">(manual)</span>}
                        </p>
                      ))}
                  </div>
                </>
              )}
              {canCloseOutNow && (
                <div className="pt-1 border-t border-amber-200">
                  <button
                    type="button"
                    onClick={closeOutNow}
                    disabled={closingOut}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
                    title="Registra la salida del empleado con la hora actual"
                  >
                    {closingOut && <LoadingSpinner size="sm" />} Cerrar salida ahora
                  </button>
                  <p className="text-[11px] text-amber-700 mt-1">
                    El empleado tiene entrada sin cerrar. Pulsando se registra la salida con la hora actual del sistema.
                  </p>
                </div>
              )}
            </div>
          ) : null}
          {isEdit ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                <input type="date" className={inputClass} value={editDate} onChange={(e) => setEditDate(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora de entrada</label>
                  <input type="time" className={inputClass} value={editStart} onChange={(e) => setEditStart(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora de salida</label>
                  <input type="time" className={inputClass} value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                <textarea rows={2} className={inputClass} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha base</label>
                <input type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>

              {/* Pattern presets */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Patrón de jornada</label>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(PRESETS) as Pattern[]).map((p) => (
                    <button key={p} type="button" onClick={() => choosePattern(p)}
                      className={`px-3 py-1.5 rounded-lg border text-sm ${pattern === p ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {PATTERN_LABEL[p]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Slots editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tramos del día</label>
                <div className="space-y-2">
                  {slots.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="time" className={inputClass} value={s.start} onChange={(e) => updateSlot(i, 'start', e.target.value)} />
                      <span className="text-gray-400">–</span>
                      <input type="time" className={inputClass} value={s.end} onChange={(e) => updateSlot(i, 'end', e.target.value)} />
                      {slots.length > 1 && (
                        <button type="button" onClick={() => removeSlot(i)} className="text-gray-400 hover:text-red-600 p-1">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addSlot}
                    className="inline-flex items-center gap-1 text-sm text-vida-600 hover:text-vida-700">
                    <PlusIcon className="h-4 w-4" /> Añadir tramo
                  </button>
                </div>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Aplicar a</label>
                <div className="flex flex-wrap gap-2">
                  {(['day', 'week', 'month'] as Scope[]).map((s) => (
                    <button key={s} type="button" onClick={() => setScope(s)}
                      className={`px-3 py-1.5 rounded-lg border text-sm ${scope === s ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {s === 'day' ? 'Solo este día' : s === 'week' ? 'Toda esta semana' : 'Todo este mes'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Weekday picker (only when scope > day) */}
              {scope !== 'day' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Días de la semana</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_LABEL.map((lbl, i) => (
                      <button key={lbl} type="button"
                        onClick={() => setWeekdays((w) => w.map((b, idx) => (idx === i ? !b : b)))}
                        className={`px-3 py-1.5 rounded-lg border text-sm ${weekdays[i] ? 'border-vida-600 bg-vida-50 text-vida-700' : 'border-gray-200 text-gray-400 hover:bg-gray-50'}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Se crearán {itemCount} turno{itemCount === 1 ? '' : 's'} en total ({targetDates.length} día{targetDates.length === 1 ? '' : 's'} × {slots.length} tramo{slots.length === 1 ? '' : 's'}).
                  </p>
                </div>
              )}

              {scope === 'day' && (
                <p className="text-xs text-gray-400">
                  Se creará{slots.length === 1 ? '' : 'n'} {slots.length} turno{slots.length === 1 ? '' : 's'} en {date}.
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                <textarea rows={2} className={inputClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 gap-2">
          {isEdit ? (
            <button onClick={doDelete} disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">
              <TrashIcon className="h-4 w-4" /> Borrar
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
            <button onClick={isEdit ? saveEdit : saveCreate} disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50">
              {saving && <LoadingSpinner size="sm" />} {isEdit ? 'Guardar' : `Crear ${itemCount} turno${itemCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
